import _ from 'lodash';
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { setResultsSearchId as setResultsSearchIdAction } from '../../../actions/search.actions';
import { SearchCompleted, type SearchCompletedProperties } from '../../../ampli';
import {
	getCourseMindItemsLoadingState,
	getDisplayedResultCount,
	getMindItemLoadingState as getMindItemLoadingStateSelector,
	getProductLoadingState as getProductLoadingStateSelector,
	getUnifiedLoadingState,
	getVideoMindItemsLoadingState as getVideoMindItemsLoadingStateSelector,
	SearchLoadingStatus,
	selectCourseResultsSearchId,
	selectCourseSearchFilters,
	selectRelatedTopicsByParams,
	selectResultsSearchId,
	selectSearchFilters,
} from '../../../reducers/search.selectors';
import services from '../../../services';
import {
	type Pagination,
	type SearchFilters,
	SearchResultsTab as SearchResultsTabEnum,
} from '../../../types/search';
import {
	getAmpliSearchEventPayload,
	getCurrentSearchId,
	type SearchEventData,
	startNewSearchJourney,
} from '../../../utils/ampli';
import searchResultsStorageManager from '../../../utils/searchResultsStorageManager';
import useAction from '../../../utils/useAction';

interface UseSearchCompletedTrackingParams {
	currentTab: SearchResultsTabEnum;
	requestFilters: SearchFilters;
	trackingPayload: SearchEventData;
	/** The results shell already subscribes to these for scroll restoration. */
	paginationByTab: Record<SearchResultsTabEnum, Pagination>;
	searchTerm?: string;
	isRelatedTopicsTreatment?: boolean;
}

const mapTabToEventName: {
	[key in SearchResultsTabEnum]: 'document' | 'product' | 'video' | 'course';
} = {
	[SearchResultsTabEnum.MINDITEMS]: 'document',
	[SearchResultsTabEnum.PRODUCTS]: 'product',
	[SearchResultsTabEnum.VIDEOS]: 'video',
	[SearchResultsTabEnum.COURSES]: 'course',
	[SearchResultsTabEnum.ALL]: 'document',
};

/**
 * Fires the `SearchCompleted` Amplitude event once per (search id, tab).
 *
 * The guards below are the whole point of this hook: each one suppresses a fire that
 * would otherwise report a *different* search than the one the user is looking at, and
 * each one also burns the (search id, tab) dedupe key when it fires wrongly — so a
 * mis-fire does not just add a bad event, it hides the correct one.
 */
const useSearchCompletedTracking = (params: UseSearchCompletedTrackingParams) => {
	const { currentTab, requestFilters, trackingPayload, paginationByTab, searchTerm } = params;

	const mindItemsDisplayedCount = useSelector((state) =>
		getDisplayedResultCount(state, 'mindItems')
	);
	const productsDisplayedCount = useSelector((state) => getDisplayedResultCount(state, 'products'));
	const videoMindItemsDisplayedCount = useSelector((state) =>
		getDisplayedResultCount(state, 'videoMindItems')
	);
	const courseMindItemsDisplayedCount = useSelector((state) =>
		getDisplayedResultCount(state, 'courseMindItems')
	);

	const mindItemLoadingState = useSelector(getMindItemLoadingStateSelector);
	const videoMindItemsLoadingState = useSelector(getVideoMindItemsLoadingStateSelector);
	const productLoadingState = useSelector(getProductLoadingStateSelector);
	const courseMindItemsLoadingState = useSelector(getCourseMindItemsLoadingState);
	const unifiedLoadingState = useSelector(getUnifiedLoadingState);

	const searchFilters = useSelector(selectSearchFilters);
	const courseSearchFilters = useSelector(selectCourseSearchFilters);
	const resultsSearchId = useSelector(selectResultsSearchId);
	const courseResultsSearchId = useSelector(selectCourseResultsSearchId);
	const setResultsSearchId = useAction(setResultsSearchIdAction);

	// Mirror RelatedTopicsPills' visibility: topics are only fetched in the
	// treatment, where the cache resolves to an array (true when chips render,
	// false when it resolved empty). Control never shows the block, so it always
	// reports false.
	const relatedTopicsParams = searchTerm
		? {
				term: searchTerm,
				subject: searchFilters?.subject,
				classYear: searchFilters?.classYear,
				schoolType: searchFilters?.schoolType,
			}
		: undefined;
	const relatedTopics = useSelector(selectRelatedTopicsByParams(relatedTopicsParams));
	const relatedTopicsDisplayed = params.isRelatedTopicsTreatment
		? Array.isArray(relatedTopics) && relatedTopics.length > 0
		: false;

	const displayedCountByTab = useMemo(
		() => ({
			[SearchResultsTabEnum.MINDITEMS]: mindItemsDisplayedCount,
			[SearchResultsTabEnum.PRODUCTS]: productsDisplayedCount,
			[SearchResultsTabEnum.VIDEOS]: videoMindItemsDisplayedCount,
			[SearchResultsTabEnum.COURSES]: courseMindItemsDisplayedCount,
			[SearchResultsTabEnum.ALL]: undefined,
		}),
		[
			mindItemsDisplayedCount,
			productsDisplayedCount,
			videoMindItemsDisplayedCount,
			courseMindItemsDisplayedCount,
		]
	);

	const loadingStateByTab = useMemo(
		() => ({
			[SearchResultsTabEnum.MINDITEMS]: mindItemLoadingState,
			[SearchResultsTabEnum.PRODUCTS]: productLoadingState,
			[SearchResultsTabEnum.VIDEOS]: videoMindItemsLoadingState,
			[SearchResultsTabEnum.COURSES]: courseMindItemsLoadingState,
			[SearchResultsTabEnum.ALL]: unifiedLoadingState,
		}),
		[
			mindItemLoadingState,
			productLoadingState,
			videoMindItemsLoadingState,
			courseMindItemsLoadingState,
			unifiedLoadingState,
		]
	);

	const isRelatedTopicsTreatment = params.isRelatedTopicsTreatment;

	useEffect(() => {
		// SearchPage now fires only the active tab's request (SEAR-500), so SearchCompleted fires
		// per tab when *that* tab finishes loading. The payload reports the current tab's count;
		// other tabs' counts are populated whenever the user visits them.
		const currentTabLoadingState = loadingStateByTab[currentTab];

		if (currentTabLoadingState !== SearchLoadingStatus.LOADED) return;

		// A submit mints the new search id (SearchBar) before SearchPage's effect dispatches the
		// request, and child effects commit before parent ones — so this would otherwise run
		// against the *previous* search's still-READY pages, report its total, and burn the
		// (searchId, tab) key below, suppressing the correct fire. Every START records the filters
		// its pages were fetched for, so a disagreement means the store holds the older search.
		const isCourseTab = currentTab === SearchResultsTabEnum.COURSES;
		const filtersForResults = isCourseTab ? courseSearchFilters : searchFilters;
		if (!_.isEqual(filtersForResults, requestFilters)) return;

		// Related topics fetch independently and can resolve *after* results load, so
		// firing now would record 'related topics displayed' from a stale (undefined)
		// cache. In the treatment, defer until that term's fetch settles — it resolves
		// to [] even on failure, so undefined reliably means still in flight. The
		// re-run on `relatedTopicsDisplayed` fires the event once topics settle.
		if (isRelatedTopicsTreatment && searchTerm && relatedTopics === undefined) return;

		// If no searchId exists (e.g., user navigated directly to search page without submitting a search),
		// create one now so it persists throughout the search journey
		let searchId = getCurrentSearchId();
		if (searchId) {
			// The submit handler mints the next id *before* the request for it goes out, so a
			// render can commit while the store and the URL both still describe the previous
			// search — the filters comparison above is blind to that, because both of its sides
			// are stale together. The id the pages were fetched under is not: it only advances
			// when that search's START dispatches. Without this the event goes out carrying the
			// previous search's term and count under the new id, and burns the key below so the
			// correct fire is suppressed (PRO-91).
			const idForResults = isCourseTab ? courseResultsSearchId : resultsSearchId;
			if (idForResults && idForResults !== searchId) return;
		} else {
			searchId = startNewSearchJourney();
			// These pages were fetched before any journey existed, so nothing recorded an id for
			// them. Stamp the one just minted: otherwise the next search finds no older id to
			// disagree with and its own stale window goes undetected.
			setResultsSearchId({ searchId, isCourse: isCourseTab });
		}

		// Dedupe lives at the search id's own lifetime, not the mount's: a reload keeps the id
		// (so result clicks stay attributed to the original search) while remounting this
		// component, which refired the event for a journey that had already reported.
		if (searchResultsStorageManager.hasFiredSearchCompleted(searchId, currentTab)) return;

		// Build a typed payload so the SearchCompleted constructor still validates the
		// surrounding fields. The ALL tab maps to `'document'` which isn't a key in the
		// ampli schema — we deliberately omit a results count there rather than emit an
		// unknown key. Per-tab events still fire when the user visits a concrete tab.

		// PRO-72: the API can report a non-zero total while hydration resolves no items,
		// leaving the user on the empty state — report 0 so the zero-results signal agrees
		// with what the user saw. An errored tab yields `undefined`, not 0, so an outage
		// keeps reporting the raw total instead of counting as a zero-result search.
		const displayedCount = displayedCountByTab[currentTab];
		const total = displayedCount === 0 ? 0 : paginationByTab[currentTab].total;
		const searchResultsPayload: Partial<SearchCompletedProperties> = {};
		switch (currentTab) {
			case SearchResultsTabEnum.MINDITEMS:
				searchResultsPayload['nr of minditem search results'] = total;
				break;
			case SearchResultsTabEnum.PRODUCTS:
				searchResultsPayload['nr of product search results'] = total;
				break;
			case SearchResultsTabEnum.VIDEOS:
				searchResultsPayload['nr of video search results'] = total;
				break;
			case SearchResultsTabEnum.COURSES:
				searchResultsPayload['nr of course search results'] = total;
				break;
		}
		const ampliSearchEventPayload = getAmpliSearchEventPayload(trackingPayload);

		const searchSuggestionApplied = searchResultsStorageManager.getSearchSuggestionApplied();
		const relatedTopicsApplied = searchResultsStorageManager.getRelatedTopicsApplied();

		const event = new SearchCompleted({
			// `CourseSearchPage` renders this tree with the COURSES tab, so `'course'` does go
			// out — but the generated ampli schema only allows 'document' | 'product' | 'video',
			// hence the cast. Widening it means an Amplitude tracking-plan edit plus an
			// `ampli-pull` regenerate (`src/ampli/index.ts` is generated — never hand-edited).
			'search results tab': mapTabToEventName[
				currentTab
			] as SearchCompletedProperties['search results tab'],
			...searchResultsPayload,
			'search term': ampliSearchEventPayload['search term'],
			'search filter': ampliSearchEventPayload['search filter'] ?? [],
			'app section': ampliSearchEventPayload['app section'],
			'search algorithm': ampliSearchEventPayload['search algorithm'],
			'search id': searchId,
			...(searchSuggestionApplied != null && { 'search suggestion applied': searchSuggestionApplied }),
			...(relatedTopicsApplied != null && { 'related topics applied': relatedTopicsApplied }),
			'related topics displayed': relatedTopicsDisplayed,
		});

		services.track.ampliEventTrack({ event });

		// Burn the key only once the event is actually out. `ampliEventTrack` calls
		// `ampli.track` + `flush` synchronously, so a throw there with the key already marked
		// would lose this journey's event for good — the exact failure mode the guards above
		// exist to prevent. Marking after is safe: the effect body never re-enters.
		searchResultsStorageManager.markSearchCompletedFired(searchId, currentTab);
	}, [
		currentTab,
		loadingStateByTab,
		displayedCountByTab,
		paginationByTab,
		trackingPayload,
		searchTerm,
		requestFilters,
		isRelatedTopicsTreatment,
		relatedTopics,
		relatedTopicsDisplayed,
		searchFilters,
		courseSearchFilters,
		resultsSearchId,
		courseResultsSearchId,
		setResultsSearchId,
	]);
};

export default useSearchCompletedTracking;
